import apiClient from '@/api/apiClient';
import type { Account } from '@/types/account';
import type { PlatformCountry } from '@/platformCountry';
import type { Regulator } from '@/types/regulators';
import type { ProfileGroup } from '@/types/risk-profile';

export interface KyxRiskProfilePricingUpdatePayload {
  policy_id: string;
  base_token_price: number;
}

export interface KyxRiskProfilePricingUpdateResult {
  policy_id: string;
  policy_name: string;
  base_token_price: number;
  instances_updated: number;
}

export default {
  // Accounts
  getAccounts(): Promise<Account[]> {
    return apiClient.get('/system-admin/accounts');
  },
  createAccount(data: { name: string; cnpj: string; platform_country_code: PlatformCountry }): Promise<Account> {
    return apiClient.post('/system-admin/accounts', data);
  },
  setAccountPlatformCountry(
    accountId: string,
    platformCountryCode: PlatformCountry,
  ): Promise<{ platform_country_code: PlatformCountry }> {
    return apiClient.put(`/system-admin/accounts/${accountId}/platform-country`, {
      platform_country_code: platformCountryCode,
    });
  },
  deleteAccount(accountId: string): Promise<void> {
    return apiClient.delete(`/system-admin/accounts/${accountId}`);
  },

  // Regulators the account is obligated to report to (Layer B)
  getAccountRegulators(accountId: string): Promise<Regulator[]> {
    return apiClient.get(`/system-admin/accounts/${accountId}/regulators`);
  },
  setAccountRegulators(accountId: string, slugs: string[]): Promise<Regulator[]> {
    return apiClient.put(`/system-admin/accounts/${accountId}/regulators`, { slugs });
  },

  getAccountRiskProfiles(accountId: string): Promise<ProfileGroup[]> {
    return apiClient.get(`/system-admin/accounts/${accountId}/risk-profiles`);
  },

  updateRiskProfilePricing(
    accountId: string,
    payload: KyxRiskProfilePricingUpdatePayload,
  ): Promise<KyxRiskProfilePricingUpdateResult> {
    return apiClient.patch(`/system-admin/accounts/${accountId}/risk-profiles/pricing`, payload);
  },

  // Unified Workflows (Managed via /system-admin/workflows)
  getAccountWorkflows(accountId: string): Promise<any[]> {
    return apiClient.get('/system-admin/workflows', { params: { account_id: accountId } });
  },
  getWorkflowTemplates(lifecycleStatus?: string): Promise<any[]> {
    const params = lifecycleStatus ? { lifecycle_status: lifecycleStatus } : undefined;
    return apiClient.get('/system-admin/workflows', { params }); // account_id is null
  },
  createFullWorkflow(accountId: string | null, data: any): Promise<any> {
    const params = accountId ? { account_id: accountId } : undefined;
    return apiClient.post('/system-admin/workflows', data, { params });
  },
  updateFullAccountWorkflow(accountId: string | null, workflowId: string, data: any): Promise<any> {
    const params = accountId ? { account_id: accountId } : undefined;
    return apiClient.put(`/system-admin/workflows/${workflowId}`, data, { params });
  },
  deleteAccountWorkflow(accountId: string | null, workflowId: string): Promise<void> {
    const params = accountId ? { account_id: accountId } : undefined;
    return apiClient.delete(`/system-admin/workflows/${workflowId}`, { params });
  },

  // Legacy/Compatibility Wrappers (mapping to new unified endpoints)
  createWorkflowTemplate(data: any): Promise<any> {
    return this.createFullWorkflow(null, data);
  },
  updateFullWorkflowTemplate(workflowId: string, data: any): Promise<any> {
    return this.updateFullAccountWorkflow(null, workflowId, data);
  },
  deleteWorkflowTemplate(workflowId: string): Promise<void> {
    return this.deleteAccountWorkflow(null, workflowId);
  },

  // Roles (Managed via /system-admin/accounts/{id}/account-roles)
  getAccountRoles(accountId: string): Promise<any[]> {
    return apiClient.get(`/system-admin/accounts/${accountId}/account-roles`);
  },
  createAccountRole(accountId: string, data: any): Promise<any> {
    return apiClient.post(`/system-admin/accounts/${accountId}/account-roles`, data);
  },
  updateAccountRole(accountId: string, roleId: string, data: any): Promise<any> {
    return apiClient.patch(`/system-admin/accounts/${accountId}/account-roles/${roleId}`, data);
  },
  deleteAccountRole(accountId: string, roleId: string): Promise<void> {
    return apiClient.delete(`/system-admin/accounts/${accountId}/account-roles/${roleId}`);
  },
};
